// src/game-system/character/backgrounds.js

/**
 * @typedef {Object} Background
 * @property {string} name - Display name of the background
 * @property {string} description - Brief description of the background
 * @property {Array<{category: string, skill: string, bonus: number}>} skillBonuses - Skill bonuses from this background
 * @property {Object} attributeModifiers - Optional attribute modifiers
 * @property {Array<string>} startingEquipment - Starting equipment provided by this background
 * @property {Array<string>} storyPrompts - Backstory prompts related to this background
 */

/**
 * Backgrounds provide skill bonuses and story prompts to characters
 * Each background provides bonuses to a set of skills, possible attribute modifiers,
 * and suggested story elements for character creation.
 * 
 * @type {Object.<string, Background>}
 */
export const BACKGROUNDS = {
  scholar: {
    name: "Scholar",
    description: "You spent years learning the secrets of the world in libraries and universities.",
    skillBonuses: [
      { category: "knowledge", skill: "history", bonus: 2 },
      { category: "knowledge", skill: "arcana", bonus: 2 },
      { category: "magic", skill: "elementalMagic", bonus: 1 },
      { category: "social", skill: "persuasion", bonus: 1 }
    ],
    attributeModifiers: {
      intelligence: 1
    },
    startingEquipment: [
      "Scholar's robe",
      "Journal and quill",
      "Reading glasses",
      "Collection of ancient texts"
    ],
    storyPrompts: [
      "What field of knowledge did you study?",
      "What rare piece of information do you possess?",
      "Why did you leave academic life for adventure?",
      "Which library or university was your home?"
    ]
  },
  
  soldier: {
    name: "Soldier",
    description: "You were trained in the art of warfare and served in a military organization.",
    skillBonuses: [
      { category: "combat", skill: "oneHandedWeapons", bonus: 2 },
      { category: "combat", skill: "twoHandedWeapons", bonus: 1 },
      { category: "survival", skill: "athletics", bonus: 2 },
      { category: "social", skill: "intimidation", bonus: 1 }
    ],
    attributeModifiers: {
      strength: 1
    },
    startingEquipment: [
      "Military uniform",
      "Insignia of rank",
      "Polishing kit",
      "Set of dice or cards"
    ],
    storyPrompts: [
      "Which military unit did you serve with?",
      "Did you fight in any notable battles?",
      "Why did you leave military service?",
      "Do you still maintain connections with your former comrades?"
    ]
  },
  
  merchant: {
    name: "Merchant",
    description: "You made your living by trading goods across different regions.",
    skillBonuses: [
      { category: "social", skill: "persuasion", bonus: 2 },
      { category: "social", skill: "deception", bonus: 1 },
      { category: "knowledge", skill: "economics", bonus: 2 },
      { category: "knowledge", skill: "geography", bonus: 1 }
    ],
    attributeModifiers: {
      charisma: 1
    },
    startingEquipment: [
      "Fine clothes",
      "Set of scales",
      "Ledger book",
      "Letter of credit from a guild"
    ],
    storyPrompts: [
      "What goods did you specialize in trading?",
      "Which markets or trade routes are you familiar with?",
      "Did you lose a fortune or make one?",
      "What exotic places have you visited for trade?"
    ]
  },
  
  criminal: {
    name: "Criminal",
    description: "You lived outside the law, using stealth and cunning to survive.",
    skillBonuses: [
      { category: "thievery", skill: "stealth", bonus: 2 },
      { category: "thievery", skill: "lockpicking", bonus: 2 },
      { category: "social", skill: "deception", bonus: 1 },
      { category: "survival", skill: "perception", bonus: 1 }
    ],
    attributeModifiers: {
      dexterity: 1
    },
    startingEquipment: [
      "Dark, hooded clothing",
      "Set of thieves' tools",
      "Small knife",
      "Fake identity papers"
    ],
    storyPrompts: [
      "What type of criminal activities did you engage in?",
      "Are you wanted by the authorities somewhere?",
      "Did you work alone or as part of a gang?",
      "What was your biggest heist or con?"
    ]
  },
  
  healer: {
    name: "Healer",
    description: "You dedicated your life to healing the wounds and illnesses of others.",
    skillBonuses: [
      { category: "magic", skill: "restorationMagic", bonus: 2 },
      { category: "knowledge", skill: "medicine", bonus: 2 },
      { category: "social", skill: "empathy", bonus: 2 },
      { category: "crafting", skill: "alchemy", bonus: 1 }
    ],
    attributeModifiers: {
      wisdom: 1
    },
    startingEquipment: [
      "Healer's kit",
      "Mortar and pestle",
      "Herbs pouch",
      "Clean bandages"
    ],
    storyPrompts: [
      "Where did you learn your healing arts?",
      "Have you ever failed to save someone important to you?",
      "What motivated you to help others?",
      "Do you have a specialty within healing?"
    ]
  },
  
  artisan: {
    name: "Artisan",
    description: "You mastered a craft and created works that others value and admire.",
    skillBonuses: [
      { category: "crafting", skill: "smithing", bonus: 2 },
      { category: "crafting", skill: "enchanting", bonus: 1 },
      { category: "knowledge", skill: "appraisal", bonus: 2 },
      { category: "social", skill: "persuasion", bonus: 1 }
    ],
    attributeModifiers: {
      dexterity: 1
    },
    startingEquipment: [
      "Craftsman's tools",
      "Fine examples of your work",
      "Guild membership",
      "Apron and gloves"
    ],
    storyPrompts: [
      "What craft did you specialize in?",
      "Did you create any particularly famous works?",
      "Why did you leave your workshop behind?",
      "Who taught you your craft?"
    ]
  },
  
  explorer: {
    name: "Explorer",
    description: "You traveled to distant and dangerous places, mapping the unknown.",
    skillBonuses: [
      { category: "survival", skill: "athletics", bonus: 1 },
      { category: "survival", skill: "survival", bonus: 2 },
      { category: "knowledge", skill: "geography", bonus: 2 },
      { category: "knowledge", skill: "nature", bonus: 1 }
    ],
    attributeModifiers: {
      constitution: 1
    },
    startingEquipment: [
      "Maps and charts",
      "Compass",
      "Sturdy boots",
      "Specimen collection kit"
    ],
    storyPrompts: [
      "What was the most dangerous place you've explored?",
      "What discovery are you most proud of?",
      "Are you running toward something or away from something?",
      "What drives your wanderlust?"
    ]
  },
  
  noble: {
    name: "Noble",
    description: "You were born into privilege and raised in the courts of power.",
    skillBonuses: [
      { category: "social", skill: "persuasion", bonus: 2 },
      { category: "social", skill: "etiquette", bonus: 2 },
      { category: "knowledge", skill: "history", bonus: 1 },
      { category: "knowledge", skill: "politics", bonus: 1 }
    ],
    attributeModifiers: {
      charisma: 1
    },
    startingEquipment: [
      "Fine clothes",
      "Signet ring",
      "Letter of introduction",
      "Set of ceremonial weapons"
    ],
    storyPrompts: [
      "What is your family known for?",
      "Do you embrace or reject your noble heritage?",
      "What responsibilities did you leave behind?",
      "Do you have any enemies among other noble houses?"
    ]
  }
};

/**
 * Apply background bonuses to a character's skills and attributes
 * 
 * @param {Object} character - The character object to modify
 * @param {string} backgroundId - ID of the background to apply
 * @returns {Object} Modified character object with applied bonuses
 */
export function applyBackgroundBonuses(character, backgroundId) {
  if (!BACKGROUNDS[backgroundId]) {
    console.error(`Invalid background: ${backgroundId}`);
    return character;
  }

  const background = BACKGROUNDS[backgroundId];
  const updatedCharacter = { ...character };
  
  // Apply skill bonuses
  if (!updatedCharacter.skills) {
    updatedCharacter.skills = {};
  }
  
  background.skillBonuses.forEach(bonus => {
    if (!updatedCharacter.skills[bonus.category]) {
      updatedCharacter.skills[bonus.category] = {};
    }
    
    if (!updatedCharacter.skills[bonus.category][bonus.skill]) {
      updatedCharacter.skills[bonus.category][bonus.skill] = 0;
    }
    
    updatedCharacter.skills[bonus.category][bonus.skill] += bonus.bonus;
  });
  
  // Apply attribute modifiers if any
  if (background.attributeModifiers) {
    if (!updatedCharacter.attributes) {
      updatedCharacter.attributes = {};
    }
    
    Object.entries(background.attributeModifiers).forEach(([attribute, value]) => {
      if (!updatedCharacter.attributes[attribute]) {
        updatedCharacter.attributes[attribute] = 10; // Default base attribute
      }
      updatedCharacter.attributes[attribute] += value;
    });
  }
  
  // Add starting equipment
  if (background.startingEquipment && background.startingEquipment.length > 0) {
    if (!updatedCharacter.equipment) {
      updatedCharacter.equipment = { items: [] };
    }
    
    updatedCharacter.equipment.items = [
      ...updatedCharacter.equipment.items,
      ...background.startingEquipment
    ];
  }
  
  // Set background
  updatedCharacter.background = backgroundId;
  
  return updatedCharacter;
}

/**
 * Get the skill bonuses for a specific background
 * 
 * @param {string} backgroundId - ID of the background
 * @returns {Array<{category: string, skill: string, bonus: number}>} Array of skill bonuses
 */
export function getBackgroundSkillBonuses(backgroundId) {
  if (!BACKGROUNDS[backgroundId]) {
    console.error(`Invalid background: ${backgroundId}`);
    return [];
  }
  
  return BACKGROUNDS[backgroundId].skillBonuses;
}

/**
 * Get all backgrounds that provide a bonus to a specific skill
 * 
 * @param {string} category - Skill category
 * @param {string} skill - Skill name
 * @returns {Array<string>} Array of background IDs that provide the bonus
 */
export function getBackgroundsWithSkillBonus(category, skill) {
  return Object.entries(BACKGROUNDS)
    .filter(([_, background]) => 
      background.skillBonuses.some(
        bonus => bonus.category === category && bonus.skill === skill
      )
    )
    .map(([id, _]) => id);
}